import React, { ChangeEvent, FC, forwardRef, ReactNode } from 'react';
import { MultiValue } from 'react-select';

import InputField from '@components/InputField/ui/InputField';
import TextareaField from '@components/TextareaField/ui/TextareaField';
import { UploadForm } from '@components/index';
import { Selector } from '@components/Selector/ui/Selector';
import { Controller } from 'react-hook-form';
import { ImageFile } from '@features/DemandFormTemplates/model/DemandForm';
import { CaseFile } from '@api/document/model/types';
import { InputSelector } from '@components/InputSelector/ui/InputSelector';
import CustomSelectField from '@components/CustomSelect/ui/CustomSelect';
import PasswordInputField from '@components/PasswordInputField/ui/PasswordInputField';
import ReactSelect from '@components/StyledReactSelect/StyledReactSelect';
import { Option } from '@utils/react-select.ts';
import Tooltip from '@mui/material/Tooltip';
import { IconHelp } from '@tabler/icons-react';
import { NumericFormat } from 'react-number-format';
import { InputAdornment, TextField } from '@mui/material';
import { DocumentGeneratorInstanceVersionFile } from '@app/types/document-instance';
import styles from './FormInput.module.scss';

interface IFormInputProps {
  labelText?: string;
  name: string;
  placeholder?: string;
  required?: boolean;
  errorText?: any;
  tabIndex?: number;
  className?: string;
  inputClassname?: string;
  inputWrapperClassName?: string;
  fileBlockClassname?: string;
  type?:
    | 'input'
    | 'textarea'
    | 'select'
    | 'upload'
    | 'password'
    | 'inputSelect'
    | 'number'
    | 'customSelect'
    | 'reactSelect'
    | 'currency';
  image?: ReactNode;
  ref?: any;
  tooltipText?: string;
  values?: string[];
  files?:
    | File[]
    | CaseFile[]
    | SectionFile[]
    | DocumentGeneratorInstanceVersionFile[];
  imageFiles?: ImageFile[];
  onFileUpload?: (file: any) => void;
  onFileAction?: (type: 'upload' | 'remove', data?: any) => void;
  bordered?: boolean;
  onResize?: (height: any) => void;
  autoComplete?: boolean;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  pattern?: string;
  onKeyUp?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  sizeLimit?: number;
  isImage?: boolean;
  isAudio?: boolean;
  isAudioText?: boolean;
  isUploadButton?: boolean;
  multiple?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
  value?: string;
  control?: any;
  options?: MultiValue<Option>;
  isClearable?: boolean;
  isLoading?: boolean;
  loadingText?: string;
  onChange?: (e: ChangeEvent<HTMLTextAreaElement>) => void;
}

/**
 * @param props - The props for the component.
 * @param labelText - label text for the input.
 * @param name - name for the input.
 * @param placeholder - placeholder for the input.
 * @param required - Indicates if input is required.
 * @param errorText - errorText for the input.
 * @param tabIndex - tab index for the input.
 * @param className - custom class name for the input container.
 * @param inputClassname - custom class name for the input.
 * @param inputWrapperClassName - custom class name for the input wrapper.
 * @param type - input type 'input' | 'textarea' | 'select' | 'upload' | 'password'.
 * @param image - An image or icon in input.
 * @param ref - ref for the input.
 * @param tooltipText - tooltip text for the input.
 * @param values - values for select type input.
 * @param files - files for upload type input.
 * @param onFileUpload - Function which is called after uploading file on upload type input
 * @param bordered - Indicates whether input should have a border
 * @param onResize - Function which is called after resizing textarea type input
 * @param autoComplete - Indicates if input need to auto complete.
 * @description - Form input shared component for forms
 * @returns A React component that displays an input.
 */

const FormInput: FC<IFormInputProps> = forwardRef((props, ref) => {
  const {
    labelText,
    name,
    placeholder,
    errorText,
    image,
    tabIndex,
    className,
    inputClassname,
    fileBlockClassname,
    required = false,
    type = 'input',
    tooltipText,
    values,
    files,
    imageFiles,
    onFileUpload,
    onFileAction,
    inputWrapperClassName,
    bordered = true,
    onResize,
    onKeyUp,
    sizeLimit,
    isImage,
    isAudio,
    isAudioText,
    multiple,
    isUploadButton,
    readOnly,
    disabled,
    autoComplete,
    control,
    value,
    options,
    isClearable,
    isLoading,
    loadingText,
    ...rest
  } = props;

  return (
    <div
      className={[
        styles.formInput,
        !labelText && !bordered && styles.tableInput,
        className,
      ].join(' ')}
    >
      {labelText && (
        <div className={styles.label}>
          <p className={styles.text}>
            {labelText}
            {required && <span> *</span>}
          </p>
          <div className={styles.tooltip}>
            {tooltipText && (
              <Tooltip
                title={tooltipText}
                arrow
                className={styles.tooltip}
                color="gray"
              >
                <IconHelp size={14} />
              </Tooltip>
            )}
          </div>
        </div>
      )}
      {type === 'upload' && (
        <UploadForm
          files={files}
          isImage={isImage}
          isAudio={isAudio}
          isAudioText={isAudioText}
          multiple={multiple}
          sizeLimit={sizeLimit}
          onFileUpload={onFileUpload}
          onFileAction={onFileAction}
          className={inputClassname}
          fileBlockClassname={fileBlockClassname}
          isLoading={isLoading}
          loadingText={loadingText}
          disabled={disabled}
        />
      )}

      {type === 'reactSelect' && (
        <Controller
          name={name}
          control={control}
          render={({ field }) => {
            return (
              <ReactSelect
                isClearable={isClearable}
                placeholder={placeholder}
                options={options}
                error={!!errorText}
                isDisabled={disabled}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                ref={field.ref}
              />
            );
          }}
        />
      )}

      {(type === 'select' || type === 'inputSelect') && (
        <Controller
          name={name}
          control={control}
          render={({ field }) => {
            const Component = type === 'select' ? Selector : InputSelector;
            return (
              <Component
                placeholder={placeholder}
                values={values!}
                selectorClassname={[
                  inputWrapperClassName,
                  styles.selector,
                  !!labelText && styles.defaultSelector,
                  !!errorText && styles.selectorError,
                ].join(' ')}
                optionsClassname={styles.options}
                chevronDownClassname={styles.chevronDownClassname}
                {...field}
              />
            );
          }}
        />
      )}

      {(type === 'input' || type === 'number') && (
        <InputField
          image={image}
          className={inputClassname}
          inputWrapperClassName={inputWrapperClassName}
          tabIndex={tabIndex}
          name={name}
          placeholder={placeholder!}
          error={!!errorText}
          tableInput={!labelText && !bordered}
          autoComplete={autoComplete}
          disabled={disabled}
          {...rest}
          type={type}
          ref={ref}
        />
      )}

      {type === 'password' && (
        <PasswordInputField
          className={inputClassname}
          inputWrapperClassName={inputWrapperClassName}
          tabIndex={tabIndex}
          name={name}
          placeholder={placeholder!}
          error={!!errorText}
          tableInput={!labelText && !bordered}
          {...rest}
          ref={ref}
        />
      )}

      {type === 'textarea' && (
        <TextareaField
          bordered={bordered}
          className={inputClassname}
          inputWrapperClassName={inputWrapperClassName}
          tabIndex={tabIndex}
          name={name}
          onResize={onResize}
          placeholder={placeholder!}
          error={!!errorText}
          onKeyUp={onKeyUp}
          isUploadButton={isUploadButton}
          files={files}
          onFileAction={onFileAction}
          onFileUpload={onFileUpload}
          readOnly={readOnly}
          {...rest}
          ref={ref}
        />
      )}

      {type === 'customSelect' && (
        <CustomSelectField
          control={control}
          className={[
            inputClassname,
            inputWrapperClassName,
            styles.selector,
            !!labelText && styles.defaultSelector,
            !!errorText && styles.selectorError,
          ].join(' ')}
          options={values || []}
          name={name}
          placeholder={placeholder!}
          error={!!errorText}
          {...rest}
          ref={ref}
        />
      )}

      {type === 'currency' && control && (
        <Controller
          name={name}
          control={control}
          render={({ field: { ref, onChange, ...other } }) => (
            <NumericFormat
              {...other}
              customInput={TextField}
              thousandSeparator=","
              decimalSeparator="."
              decimalScale={2}
              allowNegative={false}
              placeholder={placeholder}
              variant="outlined"
              error={!!errorText}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              }}
              onValueChange={({ floatValue }) => {
                onChange(floatValue);
              }}
            />
          )}
        />
      )}

      {errorText && <p className={styles.inputError}>{errorText}</p>}
    </div>
  );
});

export { FormInput };
